// canvas/CanvasStage.js
import React, { useEffect, useRef, useState } from "react";
import { Stage, Layer, Transformer, Rect, Text } from "react-konva";
import FloorPlanTable from "./FloorPlanTable";
import { createTextareaForText } from "src/utils/canvasTextEditingUtils";
import { clearSelectTransform, handleClickTable, handleDeselect, handleDragEnd, handleMouseDown, handleMouseMove, handleMouseUp, handleSelect, handleTransform, handleTransformImg, handleTransformImgOrViewport, handleWheel } from "src/utils/canvasHandlers";
import { getNormalizedRect } from "src/utils/canvasGeometryUtils";
import { useTheme } from "@mui/material";
import FloorPlanButton from "./FloorPlanButton";
import FloorPlanImage from "./FloorPlanImage";
import FloorPlanViewport from "./FloorPlanViewport";

const CanvasStage = ({
    isManagementSection = true,
    isViewportMobile = true,
    stageSize,
    floorPlans,
    reservations,
    setFloorPlans,
    setSelectedIds,
    selectedArea,
    stageRef,
    layerRef,
    shapeRefs,
    transformerRef,
    imageTransformerRef,
    mode,
    selectedIds,
    addTextToPlan,
    addRectToPlan,
    removeShapesFromPlan,
    toggleButton,
    isEditing = true
}) => {

    const theme = useTheme();
    const [isPanning, setIsPanning] = useState(false);
    const lastPos = useRef({ x: 0, y: 0 });

    const [drawingShape, setDrawingShape] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const viewportType = isViewportMobile ? "mobile" : "desktop";

    useEffect(() => {
        const stage = stageRef.current.getStage();
        const container = stage.container();

        const handleMouseDown = (e) => {
            if (e.button === 1 && !document.querySelector('textarea')) {
                e.preventDefault();
                setIsPanning(true);
                lastPos.current = {
                    x: e.clientX,
                    y: e.clientY,
                };
            }
        };

        const handleMouseMove = (e) => {
            if (!isPanning) return;

            const dx = e.clientX - lastPos.current.x;
            const dy = e.clientY - lastPos.current.y;

            const currentPos = stage.position();
            stage.position({
                x: currentPos.x + dx,
                y: currentPos.y + dy,
            });
            stage.batchDraw();

            lastPos.current = {
                x: e.clientX,
                y: e.clientY,
            };
        };

        const handleMouseUp = (e) => {
            if (e.button === 1) {
                setIsPanning(false);
            }
        };

        container.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            container.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isPanning]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Delete") {
                removeShapesFromPlan();
                setSelectedIds([]);
                if (transformerRef.current) {
                    transformerRef.current.nodes([]);
                }
                if (imageTransformerRef.current) {
                    imageTransformerRef.current.nodes([]);
                }
            }
        };

        if (isEditing) {
            window.addEventListener("keydown", handleKeyDown);
            return () => window.removeEventListener("keydown", handleKeyDown);
        }
    }, [selectedIds]);

    const isDraggable = isEditing && mode === 'select';

    const handleSelectShape = (e, id) => {
        if (selectedIds.length === 1 && floorPlans[selectedArea?.id]?.find((shape) => shape.id === selectedIds[0])?.type === "image") {
            clearSelectTransform(setSelectedIds, imageTransformerRef);
        }
        handleSelect(e, id, mode, setSelectedIds, shapeRefs, transformerRef);
    }

    return (
        <Stage
            width={stageSize.width}
            height={stageSize.height}
            ref={stageRef}
            onMouseDown={(e) => {
                if (isEditing) {
                    handleDeselect(e, setSelectedIds, transformerRef, imageTransformerRef);
                    handleMouseDown(e, mode, setIsDrawing, setDrawingShape, setSelectedIds, addTextToPlan, stageRef)
                }
            }}
            onMouseMove={(e) => {
                if (isEditing) handleMouseMove(e, isDrawing, drawingShape, setDrawingShape, stageRef)
            }}
            onMouseUp={(e) => {
                if (isEditing) handleMouseUp(e, isDrawing, drawingShape, setIsDrawing, setDrawingShape, selectedArea, setSelectedIds, floorPlans, addRectToPlan, shapeRefs, transformerRef)
            }}
            onWheel={(e) => handleWheel(e, stageRef)}
        >
            <Layer>
                {(floorPlans[selectedArea?.id] || [])
                    .filter((shape) => shape.type === "image")
                    .map((shape) => (
                        <FloorPlanImage
                            key={shape.id}
                            shape={shape}
                            shapeRefs={shapeRefs}
                            isSelected={selectedIds.some(selectedId => selectedId === shape.id)}
                            onSelect={(e) => {
                                clearSelectTransform(setSelectedIds, transformerRef)
                                handleSelect(e, shape.id, mode, setSelectedIds, shapeRefs, imageTransformerRef)
                            }}
                            onDragEnd={(e) => handleDragEnd(e, shape.id, selectedIds, selectedArea, floorPlans, setFloorPlans, shapeRefs)}
                            onTransformEnd={(e) => handleTransformImgOrViewport(shape.id, floorPlans, setFloorPlans, setSelectedIds, selectedArea, shapeRefs, imageTransformerRef)}
                        />
                    ))}
                {selectedIds && (
                    <Transformer
                        ref={imageTransformerRef}
                        rotateEnabled={isManagementSection}
                        enabledAnchors={
                            selectedIds.length === 1 && [
                                "top-left",
                                "top-right",
                                "bottom-left",
                                "bottom-right"
                            ]}
                        boundBoxFunc={(oldBox, newBox) => {
                            if (newBox.width < 8 || newBox.height < 8) {
                                return oldBox;
                            }
                            return newBox;
                        }}
                    />
                )}

            </Layer>
            <Layer ref={layerRef}>
                {floorPlans[selectedArea?.id]?.filter((shape) => shape.type === "viewport" && shape.id.includes(viewportType))?.map((shape) => {
                    return (
                        <FloorPlanViewport
                            shape={shape}
                            shapeRefs={shapeRefs}
                            isSelected={selectedIds.some(selectedId => selectedId === shape.id)}
                            onSelect={(e) => { handleSelectShape(e, shape.id) }}
                            onDragEnd={(e) => handleDragEnd(e, shape.id, selectedIds, selectedArea, floorPlans, setFloorPlans, shapeRefs)}
                            onTransformEnd={(e) => handleTransformImgOrViewport(shape.id, floorPlans, setFloorPlans, setSelectedIds, selectedArea, shapeRefs, transformerRef)}
                        />
                    )
                })

                }
                {floorPlans[selectedArea?.id]?.map((shape) => {
                    if (shape.type === "table") {
                        const status = reservations?.find((reservation) => reservation.table === shape.id)?.status;
                        return (
                            <FloorPlanTable
                                key={shape.id}
                                table={shape}
                                mode={mode}
                                isEditing={isEditing}
                                isManagementSection={isManagementSection}
                                status={status}
                                shapeRefs={shapeRefs}
                                isSelected={isEditing ? false : selectedIds.some(selectedId => selectedId === shape.id)}
                                onSelect={(e) => {
                                    if (isEditing) {
                                        handleSelectShape(e, shape.id)
                                    } else {
                                        handleClickTable(shape.id, selectedIds, setSelectedIds)
                                    }
                                }}
                                onDragEnd={(e) => handleDragEnd(e, shape.id, selectedIds, selectedArea, floorPlans, setFloorPlans, shapeRefs)}
                                onTransformEnd={(e) => handleTransform(e, shape.id, selectedIds, floorPlans, setFloorPlans, selectedArea, shapeRefs)}
                            />
                        );
                    }
                    if (shape.type === "rect") {
                        return (
                            <Rect
                                key={shape.id}
                                {...getNormalizedRect(shape)}
                                fill="#454545"
                                cornerRadius={8}
                                draggable={isDraggable}
                                rotation={shape.rotation}
                                onClick={(e) => handleSelectShape(e, shape.id)}
                                onDragEnd={(e) => handleDragEnd(e, shape.id, selectedIds, selectedArea, floorPlans, setFloorPlans, shapeRefs)}
                                onTransformEnd={(e) => handleTransform(e, shape.id, selectedIds, floorPlans, setFloorPlans, selectedArea, shapeRefs)}
                                ref={(node) => { if (isEditing) shapeRefs.current[shape.id] = node }}
                            />
                        );
                    }
                    if (shape.type === "text") {
                        return (
                            <Text
                                key={shape.id}
                                {...shape}
                                height="auto"
                                fill="white"
                                align="center"
                                wrap="word"
                                padding={4}
                                zIndex={9999}
                                draggable={isDraggable}
                                onClick={(e) => handleSelectShape(e, shape.id)}
                                onDblClick={() => createTextareaForText(shape, shapeRefs, transformerRef, stageRef, setFloorPlans, selectedArea)}
                                onDragEnd={(e) => handleDragEnd(e, shape.id, selectedIds, selectedArea, floorPlans, setFloorPlans, shapeRefs)}
                                onTransform={(e) => {
                                    const node = e.target;
                                    const newWidth = Math.max(40, node.width() * node.scaleX());
                                    node.width(newWidth);
                                    node.scaleX(1);
                                }
                                }
                                onTransformEnd={(e) => handleTransform(e, shape.id, selectedIds, floorPlans, setFloorPlans, selectedArea, shapeRefs)}
                                ref={(node) => { if (isEditing) shapeRefs.current[shape.id] = node }}
                            />
                        );
                    }
                    if (shape.type === "button") {
                        return (
                            <FloorPlanButton
                                shape={shape}
                                draggable={isDraggable}
                                shapeRefs={shapeRefs}
                                onSelect={(e) => { handleSelectShape(e, shape.id) }}
                                onDragEnd={(e) => handleDragEnd(e, shape.id, selectedIds, selectedArea, floorPlans, setFloorPlans, shapeRefs)}
                                onTransformEnd={(e) => handleTransform(e, shape.id, selectedIds, floorPlans, setFloorPlans, selectedArea, shapeRefs)}
                                onToggle={toggleButton}
                            />
                        );
                    }
                    return null;
                })}

                {selectedIds && (
                    <Transformer
                        ref={transformerRef}
                        rotateEnabled={isManagementSection}
                        enabledAnchors={
                            selectedIds.length !== 1 ? []
                                : floorPlans[selectedArea?.id]?.find((shape) => shape.id === selectedIds[0])?.type === "rect"
                                    ? [
                                        "top-left",
                                        "top-right",
                                        "bottom-left",
                                        "bottom-right",
                                        "top-center",
                                        "bottom-center",
                                        "middle-left",
                                        "middle-right",
                                    ]
                                    : floorPlans[selectedArea?.id]?.find((shape) => shape.id === selectedIds[0])?.type === "text"
                                        ? ["middle-left", "middle-right"]
                                        : floorPlans[selectedArea?.id]?.find((shape) => shape.id === selectedIds[0])?.type === "image"
                                            ? [
                                                "top-left",
                                                "top-right",
                                                "bottom-left",
                                                "bottom-right"
                                            ]
                                            : floorPlans[selectedArea?.id]?.find((shape) => shape.id === selectedIds[0])?.type === "viewport"
                                                ? [
                                                    "top-left",
                                                    "top-right",
                                                    "bottom-left",
                                                    "bottom-right"
                                                ] : []
                        }
                        boundBoxFunc={(oldBox, newBox) => {
                            if (newBox.width < 8 || newBox.height < 8) {
                                return oldBox;
                            }
                            return newBox;
                        }}
                    />
                )}

                {isDrawing && drawingShape && drawingShape.type === "rect" && (
                    <Rect {...getNormalizedRect(drawingShape)} fill="#454545" cornerRadius={8} zIndex={9998} />
                )}

                {isDrawing && drawingShape?.type === "selection" && (
                    <Rect {...getNormalizedRect(drawingShape)} stroke={theme.palette.primary.main} />
                )}
            </Layer>
        </Stage>
    );
};

export default CanvasStage;
